import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, ExternalLink } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { MissionAssignConfirmDialog } from "@/components/missions/mission-assign-confirm-dialog"
import { MissionAssignEmptyStates } from "@/components/missions/mission-assign-empty-states"
import { MissionAssignHandpickControl } from "@/components/missions/mission-assign-handpick-control"
import { MissionAssignModePicker } from "@/components/missions/mission-assign-mode-picker"
import { MissionAssignModeSwitchDialog } from "@/components/missions/mission-assign-mode-switch-dialog"
import { MissionAssignOnBehalfConfirmDialog } from "@/components/missions/mission-assign-on-behalf-confirm-dialog"
import {
  MissionAssignOnBehalfControl,
  type OnBehalfAnswers,
} from "@/components/missions/mission-assign-on-behalf-control"
import { MissionAssignSegmentControl } from "@/components/missions/mission-assign-segment-control"
import { MissionAssignmentPreview } from "@/components/missions/mission-assignment-preview"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAssignmentUrl } from "@/hooks/use-assignment-url"
import AppLayout from "@/layouts/app-layout"
import { missionPath } from "@/routes"
import type { AssignmentMode, MissionAssignProps } from "@/types/mission-assign"

/**
 * Brand/Missions/Assign — the per-mission assignment surface (OKS-30).
 *
 * URL state-driven (`?mode=&segment_id=&crew_membership_ids[]=`) so the
 * page is shareable / refreshable / back-button-safe. Mode + selection
 * mutations re-trigger an Inertia partial reload on `preview` only.
 *
 * Three sub-controls (mode-picker / segment-control / handpick-control)
 * sit above a single `MissionAssignmentPreview`. Confirm dialog wraps
 * an Inertia `<Form>` POSTing to the same URL. The footer Cancel +
 * Confirm pair is always rendered (Confirm disabled when there's
 * nothing to assign) so the user always knows where the exits are.
 *
 * Page max-width = 960 px (UX Spec §5 explicit — diverges from OKS-29
 * Show page's 720 px because the preview list eats horizontal space).
 */
export default function MissionsAssign({
  mission,
  selected_mode,
  mode_options,
  segments,
  crew_count,
  applied_segment,
  applied_handpick_ids,
  available_memberships,
  preview,
  enrol_offer,
  already_assigned_membership_ids,
  can_confirm,
  draft_state,
  empty_crew_state,
  errors,
}: MissionAssignProps) {
  const { t } = useTranslation()

  // OKS-1042 — the enrol mode's staged answers. Local, because nothing is
  // written until the confirm dialog's single Inertia `<Form>` posts them as
  // hidden fields (UX UQ3); the URL carries only the picked creator, so the
  // `enrol_offer` partial reload can key on it.
  const [onBehalf, setOnBehalf] = useState<OnBehalfAnswers>({
    crewMembershipId: null,
    missionProductId: null,
    productVariantId: null,
    address: {},
  })

  const currentMode: AssignmentMode | null = selected_mode

  const handpickIds = applied_handpick_ids ?? []
  const segmentId = applied_segment?.id ?? null

  const {
    setMode,
    setSegment,
    toggleHandpickId,
    setHandpickIds,
    clearSelection,
    setOnBehalfMembership,
    pending,
  } = useAssignmentUrl(mission.id, {
    mode: currentMode,
    segmentId: segmentId,
    handpickIds: handpickIds,
  })

  // Mode-switch confirm dialog (UX Spec §1) — fires only when there's
  // a staged per-mode selection that would be cleared by switching.
  // No-staged-selection switches go straight through.
  const [pendingMode, setPendingMode] = useState<AssignmentMode | null>(null)

  function handleModePick(next: AssignmentMode) {
    if (next === currentMode) return
    const hasStagedSelection =
      (currentMode === "segment" && segmentId !== null) ||
      (currentMode === "handpick" && handpickIds.length > 0)

    if (hasStagedSelection) {
      setPendingMode(next)
    } else {
      setMode(next)
    }
  }

  // Picking a creator drives the `enrol_offer` partial reload: the offer is
  // per creator (its recency is), so the URL — not local state — has to carry
  // the id, or a refresh would render products with someone else's warnings.
  // ⚠️ Resolves the updater against a REF, not against the `onBehalf` state
  // variable, so a burst of changes in one tick each build on the previous one.
  // Reading the state variable here reintroduces exactly the staleness the
  // updater exists to remove.
  //
  // ⚠️ And the reload fires OUTSIDE the setState callback. React may invoke an
  // updater twice (StrictMode, concurrent re-render); a `router.get` in there
  // would be issued twice.
  const onBehalfRef = useRef(onBehalf)

  function handleOnBehalfChange(
    updater: (prev: OnBehalfAnswers) => OnBehalfAnswers,
  ) {
    const prev = onBehalfRef.current
    const next = updater(prev)
    onBehalfRef.current = next

    if (next.crewMembershipId !== prev.crewMembershipId) {
      setOnBehalfMembership(next.crewMembershipId)
    }
    setOnBehalf(next)
  }

  const onBehalfBaseError = Array.isArray(errors.base)
    ? errors.base[0]
    : errors.base

  const onBehalfCreatorLabel =
    available_memberships.find((m) => m.id === onBehalf.crewMembershipId)
      ?.handle ??
    available_memberships.find((m) => m.id === onBehalf.crewMembershipId)
      ?.name ??
    ""

  // ⚠️ Discriminated by the SERVER's own copy, not by re-deriving the refusal.
  // The link only belongs on `already_assigned`, and the alternative — checking
  // whether the creator is in `already_assigned_creators` — would answer a
  // different question (is it true NOW) than the one the refusal answered.
  const onBehalfAlreadyAssigned =
    onBehalfBaseError !== undefined &&
    onBehalfBaseError ===
      t("missions.assign.on_behalf.errors.already_assigned", {
        creator: onBehalfCreatorLabel,
      })

  const showAllAssignedEmpty =
    !empty_crew_state &&
    !draft_state &&
    preview !== null &&
    preview.new_count === 0 &&
    preview.already_assigned_count > 0

  const showFooter = !empty_crew_state && !draft_state

  // OKS-494 — block confirm when the selection exceeds the remaining gift
  // slots. Derived from `preview.gift_slots` (which rides the
  // `only:['preview']` partial reload) rather than the top-level
  // `can_confirm` prop — `can_confirm` is NOT in the partial-reload set, so
  // it goes stale across mode/selection changes; the live preview is the
  // source of truth for the over-cap state.
  const giftSlots = preview?.gift_slots ?? null
  const giftOverCap =
    giftSlots !== null && (preview?.new_count ?? 0) > giftSlots.remaining

  return (
    <AppLayout>
      <Head title={`${mission.title} — ${t("missions.assign.head_title")}`} />
      <div
        className="mx-auto flex w-full max-w-[960px] flex-col gap-6 p-6 pb-24 md:pb-6"
        data-testid="mission-assign-page"
      >
        <header className="flex flex-col gap-3">
          <Link
            href={missionPath(mission.id)}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="mission-assign-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("missions.assign.back_to_mission")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("missions.assign.head_title")}
          </h1>
        </header>

        <MissionSummaryHeader mission={mission} />

        {/*
         * Precedence: draft_state wins over empty_crew_state. Activating
         * a mission is a hard prerequisite to assignment — even with
         * crew present, no assignment is possible until the mission is
         * active. After activation, the empty-crew gate becomes the
         * natural next step. (UX Spec §3 + QA Plan §E8.)
         */}
        {draft_state && (
          <MissionAssignEmptyStates
            variant="draft_mission"
            missionId={mission.id}
          />
        )}

        {empty_crew_state && !draft_state && (
          <MissionAssignEmptyStates
            variant="zero_crew"
            missionId={mission.id}
          />
        )}

        {/*
         * Mode picker renders in the empty-crew state too (disabled),
         * per QA Plan §S7 / §E11: "mode picker disabled FE-side" so
         * assistive tech can describe the page even when there's no
         * crew to assign. Draft state still hides it (the empty draft
         * card is the only actionable surface — go activate first).
         */}
        {!draft_state && (
          <Card>
            <CardHeader>
              <CardTitle>{t("missions.assign.mode_picker.legend")}</CardTitle>
            </CardHeader>
            <CardContent>
              <MissionAssignModePicker
                value={currentMode}
                modeOptions={mode_options}
                onChange={handleModePick}
                disabled={empty_crew_state || crew_count === 0}
              />
            </CardContent>
          </Card>
        )}

        {!empty_crew_state && !draft_state && (
          <>
            {currentMode === "segment" && (
              <Card>
                <CardContent className="pt-6">
                  <MissionAssignSegmentControl
                    segments={segments}
                    appliedSegment={applied_segment}
                    onChange={setSegment}
                  />
                </CardContent>
              </Card>
            )}

            {currentMode === "handpick" && (
              <Card>
                <CardContent className="pt-6">
                  <MissionAssignHandpickControl
                    availableMemberships={available_memberships}
                    selectedIds={handpickIds}
                    onToggleId={toggleHandpickId}
                    onSetSelection={setHandpickIds}
                    onClearSelection={clearSelection}
                  />
                </CardContent>
              </Card>
            )}

            {currentMode === "on_behalf" && (
              <Card>
                <CardContent className="pt-6">
                  <MissionAssignOnBehalfControl
                    availableMemberships={available_memberships}
                    // ⚠️ NOT from `preview`: it is nil in this mode, so the
                    // AC6 disabled row silently never disabled. Server-supplied
                    // and independent of the batch preview.
                    alreadyAssignedIds={already_assigned_membership_ids}
                    offer={enrol_offer}
                    answers={onBehalf}
                    onChange={handleOnBehalfChange}
                    errors={errors}
                  />
                </CardContent>
              </Card>
            )}

            {/*
              ⚠️ The refusal's FALLBACK home. The confirm dialog unmounts on the
              302 response, so `errors.base` would have nowhere to render and
              the brand would see nothing happen — the OKS-1040 R1 defect, where
              a refusal was raised and silently discarded because its only
              surface had gone.
            */}
            {currentMode === "on_behalf" && onBehalfBaseError && (
              <Alert
                variant="destructive"
                role="alert"
                data-testid="on-behalf-orphan-refusal"
              >
                <AlertDescription>{onBehalfBaseError}</AlertDescription>
              </Alert>
            )}

            {showAllAssignedEmpty ? (
              <MissionAssignEmptyStates
                variant="all_assigned"
                missionId={mission.id}
              />
            ) : preview ? (
              <MissionAssignmentPreview preview={preview} loading={pending} />
            ) : null}
          </>
        )}

        {showFooter && (
          <div
            className="bg-background sticky bottom-0 -mx-6 flex items-center justify-end gap-2 border-t p-4 md:static md:mx-0 md:border-0 md:p-0"
            data-testid="mission-assign-footer"
          >
            <Button
              variant="outline"
              asChild
              data-testid="mission-assign-cancel"
            >
              <Link href={missionPath(mission.id)}>
                {t("missions.assign.confirm.cancel")}
              </Link>
            </Button>
            {currentMode === "on_behalf" ? (
              <MissionAssignOnBehalfConfirmDialog
                missionId={mission.id}
                answers={onBehalf}
                offer={enrol_offer}
                creatorLabel={onBehalfCreatorLabel}
                baseError={onBehalfBaseError}
                alreadyAssigned={onBehalfAlreadyAssigned}
                // Every answer present. The server floors all of it again —
                // this only keeps the brand from posting a half-filled form.
                disabled={
                  onBehalf.crewMembershipId === null ||
                  onBehalf.missionProductId === null
                }
              />
            ) : (
              <MissionAssignConfirmDialog
                missionId={mission.id}
                mode={currentMode ?? "crew"}
                segmentId={segmentId}
                handpickIds={handpickIds}
                newCount={preview?.new_count ?? 0}
                // OKS-936 — server-authoritative. `?? false` covers the
                // short-circuit states where `preview` itself is null (draft /
                // empty crew / no mode), in which the dialog is unreachable
                // anyway; it is NOT a fallback for a missing key.
                noShipAvailable={preview?.no_ship_available ?? false}
                disabled={!can_confirm || giftOverCap}
                describedBy={
                  giftOverCap
                    ? "mission-assignment-preview-gift-slots-warning"
                    : undefined
                }
              />
            )}
          </div>
        )}

        <MissionAssignModeSwitchDialog
          pendingMode={pendingMode}
          onConfirm={() => {
            if (pendingMode) setMode(pendingMode)
            setPendingMode(null)
          }}
          onCancel={() => setPendingMode(null)}
        />
      </div>
    </AppLayout>
  )
}

// Mission summary header (UX Spec §2): title h2 + type chip + status
// pill + deadline + sub-line "X creators already assigned" + View
// mission link. Card variant with compact padding.
function MissionSummaryHeader({
  mission,
}: {
  mission: MissionAssignProps["mission"]
}) {
  const { t, i18n } = useTranslation()

  const deadline = mission.deadline
    ? new Date(mission.deadline).toLocaleDateString(i18n.language, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : t("missions.assign.header.no_deadline")

  const alreadyAssignedLine =
    mission.already_assigned_count === 0
      ? t("missions.assign.header.already_assigned_zero")
      : t("missions.assign.header.already_assigned", {
          count: mission.already_assigned_count,
        })

  return (
    <Card data-testid="mission-assign-summary">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <h2
              className="text-foreground text-xl font-semibold tracking-tight"
              data-testid="mission-assign-summary-title"
            >
              {mission.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                data-testid="mission-assign-summary-type"
              >
                {t(`missions.types.${mission.mission_type}`)}
              </Badge>
              <Badge
                variant={mission.status === "active" ? "default" : "secondary"}
                className={
                  mission.status === "active"
                    ? "bg-green-600 text-white hover:bg-green-600/90"
                    : undefined
                }
                data-testid="mission-assign-summary-status"
              >
                {t(`missions.statuses.${mission.status}`)}
              </Badge>
              <span
                className="text-muted-foreground text-xs"
                data-testid="mission-assign-summary-deadline"
              >
                {deadline}
              </span>
            </div>
          </div>
          <Link
            href={missionPath(mission.id)}
            className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
            data-testid="mission-assign-summary-view-mission"
          >
            {t("missions.assign.header.view_mission")}
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
        </div>
        <p
          className="text-muted-foreground text-sm"
          data-testid="mission-assign-summary-already-assigned"
        >
          {alreadyAssignedLine}
        </p>
      </CardContent>
    </Card>
  )
}
